import { useQuery } from '@tanstack/react-query';
import { addYears, endOfDay, format, getUnixTime } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Container } from 'src/components/Container';
import { IconDatePicker } from 'src/components/IconDatePicker';
import { ListItem as MenuItem } from 'src/components/ListItem';
import { Meta } from 'src/components/Meta';
import { Typography } from 'src/components/Typography';
import {
  fetchLoggedWorkoutsForPlan,
  fetchPlanWorkoutsByDate,
  fetchPlanWorkoutsForCalendar,
} from 'src/models/leaderboard/leaderboard';
import { fetchWorkoutPlan } from 'src/models/workout-plan/workout-plan';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import toast from 'src/utils/toast';

import { LeaderboardCard } from './components/LeaderboardCard';

function utcCalendarDate(timestamp: number): Date {
  const utc = new Date(timestamp * 1000);
  return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
}

export const LeaderboardCalendarShowPage = () => {
  const { calendarId } = useParams<{ calendarId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('translation', {
    keyPrefix: 'leaderboard.public',
  });

  const { data: plan, isLoading: isLoadingPlan } = useQuery({
    queryKey: ['workoutPlan', calendarId],
    queryFn: () => fetchWorkoutPlan({ id: calendarId! }),
    enabled: !!calendarId,
  });

  const today = new Date();
  const endDate = getUnixTime(endOfDay(today));
  const startDate = getUnixTime(endOfDay(addYears(today, -1)));

  const { data: workoutsData, isLoading: isLoadingWorkouts } = useQuery({
    queryKey: ['calendarWorkouts', calendarId, startDate, endDate],
    queryFn: () =>
      fetchPlanWorkoutsForCalendar({
        planId: calendarId!,
        startDate,
        endDate,
        perPage: 50,
      }),
    enabled: !!calendarId,
  });

  const { data: loggedWorkouts } = useQuery({
    queryKey: ['loggedWorkouts', calendarId],
    queryFn: () => fetchLoggedWorkoutsForPlan(calendarId!),
    enabled: !!calendarId,
  });

  const workouts = workoutsData?.planWorkouts || [];

  const handleDateSelected = useCallback(
    async (date: Date | null) => {
      if (!date || !calendarId) return;
      const time = getUnixTime(date);
      try {
        const results = await fetchPlanWorkoutsByDate(calendarId, time);
        if (results.length) {
          navigate(`/leaderboards/public/workout/${results[0].id}`);
        } else {
          toast(t('noWorkoutForDate'));
        }
      } catch {
        toast.error(t('noWorkoutForDate'));
      }
    },
    [calendarId, navigate, t]
  );

  const isLoggedWorkout = useCallback(
    (workoutId: string) => {
      return loggedWorkouts?.some(
        (w: any) => String(w.plan_workout_id) === String(workoutId)
      );
    },
    [loggedWorkouts]
  );

  const isLoading = isLoadingPlan || isLoadingWorkouts;

  return (
    <>
      <Meta title={plan?.name || t('title')} />
      <AppFrameConfig
        id="leaderboard-calendar-show"
        containerMaxWidth="xl"
        title={plan?.name || ''}
        backButtonLinkTo="/leaderboards/public/calendars"
        backButtonTitle={t('title')}
        actions={<IconDatePicker onChange={handleDateSelected} />}
      />
      <Container>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {workouts.map((workout: any) => {
              const date = workout.calendar_date
                ? utcCalendarDate(workout.calendar_date)
                : null;
              const logged = isLoggedWorkout(workout.id);
              return (
                <LeaderboardCard
                  key={workout.id}
                  name={workout.name}
                  image={workout.workout_image || workout.image}
                  header={date ? format(date, 'MMMM d') : undefined}
                  subHeader={
                    logged
                      ? `${workout.label?.name ? workout.label.name + ' · ' : ''}✓ Logged`
                      : workout.label?.name
                  }
                  onClick={() =>
                    navigate(`/leaderboards/public/workout/${workout.id}`)
                  }
                  actions={
                    <MenuItem
                      primary={t('logWorkout')}
                      onClick={() =>
                        window.open(
                          `/workout-plans/${calendarId}/log_plan_workout?workout_id=${workout.id}`,
                          '_blank'
                        )
                      }
                    />
                  }
                />
              );
            })}
            {workouts.length === 0 && (
              <Typography sx={{ m: 2 }}>{t('noResults')}</Typography>
            )}
          </Box>
        )}
      </Container>
    </>
  );
};
